import type { ChangeEvent, ChangeEventHandler, FocusEventHandler } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { CommonInputProps } from './CommonInputProps';
import type { ClassNameProp } from '../common-types';
import { ValidationErrorMessage } from './internal';
import { Icon } from '../basic';

/**
 * Properties for a {@link DateInput} component.
 */
export type DateTimeInputProps = Omit<
  CommonInputProps<Date | null>,
  'defaultValue' | 'debounceWait'
> &
  ClassNameProp;

/**
 * Props for the internal {@link DateTimeInputRenderCmp}.
 */
type DateTimeInputRenderCmpProps = Pick<
  DateTimeInputProps,
  | 'required'
  | 'error'
  | 'disabled'
  | 'label'
  | 'className'
  | 'condensed'
  | 'readOnly'
> & {
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onInputBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus: VoidFunction;
  maxLength: number;
  pattern: string;
  placeholder: string;
};

/**
 * Internal component for the date/time input components which defines the HTML structure.
 *
 * @param properties {@link DateTimeInputCmpProps}
 * @return the created {@link JSX.Element}.
 * @constructor
 */
function DateTimeInputRenderCmp({
  className,
  required,
  disabled,
  readOnly,
  error,
  value,
  onChange,
  onFocus,
  onInputBlur,
  pattern,
  maxLength,
  placeholder,
  label: labelProp,
  condensed,
  ...props
}: Readonly<DateTimeInputRenderCmpProps>): JSX.Element {
  const label = required ? `${labelProp} *` : labelProp;
  return (
    <div
      {...props}
      className={
        getWrapperClassname(className) + (condensed ? ' condensed' : '')
      }
    >
      <div
        className={getCssClasses({
          error,
          disabled
        })}
      >
        <label>
          <span className="label">{label}</span>
          <input
            {...getInputElementProps({
              required,
              error,
              disabled,
              readOnly: readOnly
            })}
            pattern={pattern}
            placeholder={placeholder}
            maxLength={maxLength}
            value={value}
            onChange={onChange}
            onBlur={onInputBlur}
            onFocus={onFocus}
          />
          {error && (
            <Icon
              className="ehs-input-error-icon"
              aria-hidden={true}
              aria-label=""
              iconName="shui_error"
            />
          )}
        </label>
      </div>
      {error && <ValidationErrorMessage error={error} />}
    </div>
  );
}

/**
 * Props for {@link DateTimeInputCmp};
 */
type DateTimeInputCmpProps = DateTimeInputProps & {
  includeTime: boolean;
};

/**
 * Internal component shared between {@link DateInput} and {@link DateTimeInput}, which handles
 * all the special logic for these 2 components.
 *
 * @param properties
 * @constructor
 * @return the created {@link JSX.Element}.
 */
function DateTimeInputCmp({
  value,
  onChange,
  onBlur,
  onFocus,
  includeTime,
  ...props
}: Readonly<DateTimeInputCmpProps>): JSX.Element {
  const onFocusCb = useCallback(() => onFocus?.(), [onFocus]);
  const onBlurCb = useCallback(() => onBlur?.(), [onBlur]);

  const convertDateToFormattedString = useCallback(
    (date: typeof value) =>
      isValidDate(date) ? convertDateToString(date, includeTime) : '',
    [includeTime]
  );

  const allowedCharactersRegex = useMemo(
    () => (includeTime ? /^[0-9.: ]+$/ : /^[0-9.]+$/),
    [includeTime]
  );

  const [inputValue, setInputValue] = useState(
    convertDateToFormattedString(value)
  );

  const appendColonSeparator = useCallback((val: string): string => {
    let newVal = val;
    if (val.length === 13) {
      if (val[val.length - 1] === ':') {
        // prepend a 0 if colon was entered manually
        const split = val.split(' ');
        newVal = split[0] + ' 0' + split[1];
      } else {
        // automatically append colon once hours are filled
        newVal = val + ':';
      }
    }
    return newVal;
  }, []);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value;
      if (val && !allowedCharactersRegex.test(val)) {
        // prevent unexpected characters
        e.preventDefault();
        return;
      }

      if (e.target.selectionStart !== val.length) {
        // to prevent all of the magic from doing something strange when the user changes value through the mouse/selection
        // we simply update the value if the cursor position is not at the last character of the input
        setInputValue(val);
        return;
      }

      if (inputValue.length < val.length) {
        // automatically append dot if needed
        val = appendDotSeparators(val);

        // handling for spaces and colons if time is included
        if (includeTime) {
          // automatically append space if needed
          val = appendSpaceSeparator(val);

          // automatically append colon if needed
          val = appendColonSeparator(val);
        }
      } else if (
        inputValue.length > val.length &&
        ['.', ':', ' '].includes(inputValue[inputValue.length - 1])
      ) {
        // jump over the automatically added characters and treat the deleting of characters as if they aren't there
        val = val.substring(0, val.length - 1);
      }

      setInputValue(val);
    },
    [allowedCharactersRegex, appendColonSeparator, includeTime, inputValue]
  );

  const [callBlurCb, setCallBlurCb] = useState<boolean>(false);

  // effect to call the onBlur callback. Since the onChange only happens when the input container is blurred,
  // this is necessary so that consumers of this component can use the onBlur with a consistently correct input value
  useEffect(() => {
    if (callBlurCb) {
      onBlurCb?.();
      setCallBlurCb(false);
    }
  }, [callBlurCb, onBlurCb]);

  const onInputBlur = useCallback(() => {
    if (inputValue === '') {
      // we want to allow empty string for the case that a wrong date was set and the correct one is not known (e.g. birthdate)
      onChange?.(null);
      setCallBlurCb(true);
      return;
    }
    const convertedDate = convertStringToDate(inputValue, false);
    if (convertedDate) {
      onChange?.(convertedDate);
      // instead of directly calling the onBlur callback, we need to set this internal state to trigger the useEffect above
      // this is because we need the value to be updated when this blur callback is called
      setCallBlurCb(true);
    }
  }, [inputValue, onChange]);

  // effect to update internal state with the controlled value
  useEffect(() => {
    setInputValue(convertDateToFormattedString(value));
  }, [convertDateToFormattedString, value]);

  return (
    <DateTimeInputRenderCmp
      {...props}
      value={inputValue}
      onChange={handleInputChange}
      onInputBlur={onInputBlur}
      onFocus={onFocusCb}
      pattern={
        includeTime
          ? '[0-9]{2}.[0-9]{2}.[0-9]{4} [0-9]{2}:[0-9]{2}'
          : '[0-9]{2}.[0-9]{2}.[0-9]{4}'
      }
      placeholder={includeTime ? 'DD.MM.YYYY hh:mm' : 'DD.MM.YYYY'}
      maxLength={includeTime ? 16 : 10}
    />
  );
}

/**
 * A date input field which does not come with any kind of picker, but rather expects the user to type in
 * the date manually. This field also includes a Time input.
 *
 * @param props {@link DateTimeInputProps}
 * @return the created {@link JSX.Element}.
 * @constructor
 */
export function DateTimeInput(
  props: Readonly<DateTimeInputProps>
): JSX.Element {
  return <DateTimeInputCmp {...props} includeTime={true} />;
}

/**
 * A date input field which does not come with any kind of picker, but rather expects the user to type in
 * the date manually.
 *
 * @param props {@link DateTimeInputProps}
 * @return the created {@link JSX.Element}.
 * @constructor
 */
export function DateInput(props: Readonly<DateTimeInputProps>): JSX.Element {
  return <DateTimeInputCmp {...props} includeTime={false} />;
}

/**
 * Props for a {@link TimeInput} component.
 */
export type TimeInputProps = Omit<
  CommonInputProps<string>,
  'defaultValue' | 'debounceWait'
> &
  ClassNameProp;

/**
 * A time input field which does not come with any kind of picker, but rather expects the user to type in
 * the time manually, using the format "hh:mm".
 *
 * @param properties {@link TimeInputProps}
 * @constructor
 * @return the created {@link JSX.Element}.
 */
export function TimeInput({
  onFocus,
  onBlur,
  value = '',
  onChange,
  ...props
}: Readonly<TimeInputProps>): JSX.Element {
  const onFocusCb = useCallback(() => onFocus?.(), [onFocus]);
  const onBlurCb = useCallback(() => onBlur?.(), [onBlur]);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value;
      if (val && !/^[0-9:]+$/.test(val)) {
        // prevent unexpected characters
        e.preventDefault();
        return;
      }

      if (e.target.selectionStart !== val.length) {
        // to prevent all of the magic from doing something strange when the user changes value through the mouse/selection
        // we simply update the value if the cursor position is not at the last character of the input
        onChange?.(val);
        return;
      }

      if (value.length < val.length && val.length === 2) {
        if (val[val.length - 1] === ':') {
          val = '0' + val;
        } else {
          // automatically append colon once hours are filled
          val = val + ':';
        }
      } else if (value.length > val.length && value[value.length - 1] === ':') {
        // jump over the automatically added characters and treat the deleting of characters as if they aren't there
        val = val.substring(0, val.length - 1);
      }

      onChange?.(val);
    },
    [onChange, value]
  );

  return (
    <DateTimeInputRenderCmp
      {...props}
      value={value}
      onChange={handleInputChange}
      onInputBlur={onBlurCb}
      onFocus={onFocusCb}
      pattern="[0-9]{2}:[0-9]{2}"
      placeholder="hh:mm"
      maxLength={5}
    />
  );
}

/**
 * Converts the given number of a string of the given length, with as many 0s pre-pended to it to achieve said length.
 *
 * @param number the number to convert
 * @param length the target length
 * @return the converted string
 */
const convertToSpecificDigitString = (
  number: number,
  length: number
): string => {
  // Convert the number to a string
  const numberString = String(number);

  // Check if the string length is already max
  if (numberString.length === length) {
    return numberString;
  }

  // Prepend leading zeros based on the remaining length
  const leadingZeros = length - numberString.length;
  return '0'.repeat(leadingZeros) + numberString;
};

/**
 * Getter to get the css classes for the wrapper element.
 */
const getCssClasses = ({
  disabled,
  error
}: Pick<CommonInputProps<unknown>, 'error' | 'disabled'>) => {
  const cssClasses = ['ehs-input-field'];
  if (disabled) {
    cssClasses.push('disabled');
  }
  if (error) {
    cssClasses.push('invalid');
  } else {
    cssClasses.push('valid');
  }

  return cssClasses.join(' ');
};

/**
 * Getter to get the css classes for the input elements.
 */
const getInputElementProps = ({
  required,
  disabled,
  readOnly,
  error
}: Pick<
  CommonInputProps<unknown>,
  'disabled' | 'error' | 'required' | 'readOnly'
>) => {
  return {
    className: 'input ehs-date-time-input-input',
    type: 'tel',
    'aria-required': required,
    disabled: disabled,
    required: required,
    'aria-invalid': !!error,
    readOnly: readOnly,
    'aria-readonly': readOnly
  };
};

/**
 * ClassName of the most outside wrapper elements of {@link DateInput}, {@link DateTimeInput} and {@link TimeInput}.
 *
 * @param className additional className
 * @return the full className
 */
const getWrapperClassname = (className?: string) => {
  return ['ehs-input-field-wrapper', 'ehs-date-time-input-wrapper', className]
    .filter(Boolean)
    .join(' ');
};

/**
 * Converts the given value of the expected format "DD.MM.YYYY hh:mm" to a {@link Date}.
 *
 * @param value the value to convert
 * @param requireTime whether time is mandatory or not
 * @return the converted date, or `null` if conversion was impossible
 */
const convertStringToDate = (
  value: string,
  requireTime: boolean
): Date | null => {
  const splitFirst = value.split(' ');
  const splitDay = splitFirst[0]?.split('.');
  const day = splitDay?.[0];
  const month = splitDay?.[1];
  const year = splitDay?.[2];

  const splitTime = splitFirst[1]?.split(':');
  const hours = splitTime?.[0];
  const minutes = splitTime?.[1];

  if (day && month && year && (!requireTime || (hours && minutes))) {
    const yearAsNumber = Number(year);
    const newValue = new Date(
      yearAsNumber,
      Number(month) - 1,
      Number(day),
      Number(hours ?? 0),
      Number(minutes ?? 0)
    );
    // `new Date()` exhibits legacy undesirable, inconsistent behavior with two-digit year values, interpreting them as a year in the 1900s
    // to create and get dates between the years 0 and 99, instead use the preferred setFullYear() and getFullYear() methods
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#interpretation_of_two-digit_years
    if (yearAsNumber < 100) {
      newValue.setFullYear(yearAsNumber);
    }

    return newValue;
  }
  return null;
};

/**
 * This is a helper function shared between {@link DateInput} and {@link DateTimeInput}.
 * Ensure the date format of "DD.MM.YYYY" is adhered to by appending the "." character automatically, or by
 * pre-pending zeros to the day or month if a dot was entered manually.
 *
 * @param value the value to check
 * @return the modified value
 */
const appendDotSeparators = (value: string): string => {
  let newValue = value;
  if (value.length === 2 || value.length === 5) {
    if (value[value.length - 1] === '.') {
      // when typing in a '.' manually, prepend a 0 if necessary
      switch (value.length) {
        case 2:
          newValue = '0' + value;
          break;
        case 5:
          const split = value.split('.');
          newValue = split[0] + '.0' + split[1] + '.';
          break;
      }
    } else {
      // automatically append dots once day/month are filled
      newValue = value + '.';
    }
  }

  return newValue;
};

/**
 * Helper function for {@link DateTimeInput}.
 * Ensure the date format of "DD.MM.YYYY hh:mm" is adhered to by appending the space character automatically, or by
 * pre-pending zeros to the year if a space was entered manually.
 *
 * @param value the value to check
 * @return the modified value
 */
const appendSpaceSeparator = (value: string): string => {
  let newValue = value;
  if (value.length >= 8 && value.length <= 10) {
    if (value[value.length - 1] === ' ') {
      // append as many 0s as we need to reach the length of 10
      const split = value.trim().split('.');
      newValue =
        [
          split[0],
          split[1],
          convertToSpecificDigitString(Number(split[2]), 4)
        ].join('.') + ' ';
    } else if (value.length === 10) {
      newValue = value + ' ';
    }
  }

  return newValue;
};

/**
 * Checks if the given value is a valid Date.
 * @param date the Date to check.
 * @return true if valid, else false
 */
const isValidDate = (date: Date | null | undefined): date is Date =>
  !!date && !isNaN(date.valueOf());

/**
 * Converts the given Date to a string of the format "DD.MM.YYYY hh:mm", or "DD.MM.YYYY" if `includeTime` is false.
 *
 * @param date the date to format
 * @param includeTime if time should be included
 * return the formatted string
 */
const convertDateToString = (date: Date, includeTime: boolean): string => {
  const day = convertToSpecificDigitString(date.getDate(), 2);
  const month = convertToSpecificDigitString(date.getMonth() + 1, 2);
  const year = convertToSpecificDigitString(date.getFullYear(), 4);

  let result = [day, month, year].join('.');
  if (includeTime) {
    const hours = convertToSpecificDigitString(date.getHours(), 2);
    const minutes = convertToSpecificDigitString(date.getMinutes(), 2);
    result += ' ' + [hours, minutes].join(':');
  }

  return result;
};
